import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import imgUrl from '../imgUrl';
import defaultLogoLight from '../assets/icon/logo-light.svg';
import i18n from '../i18n/i18n';
import defaultLogoColor from '../assets/img/logo-color.svg';

const SettingsContext = createContext(null);

const getCurrencySymbol = (currencyCode) => {
  const map = {
    BDT: '৳',
    USD: '$',
    EUR: '€',
    GBP: '£'
  };
  return map[currencyCode] || currencyCode || '৳';
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    // Try to initialize from localStorage to avoid loading state if already cached
    const cached = localStorage.getItem('shop_settings');
    if (cached) {
      try {
        const data = JSON.parse(cached);
        const logoPath = data.shop_logo;
        const lang = data.language || 'en';
        i18n.changeLanguage(lang);
        const settingsObj = {
          shopName: data.shop_name || 'KachaBazar',
          currency: data.currency || 'BDT',
          currencySymbol: getCurrencySymbol(data.currency || 'BDT'),
          language: lang,
          shopLogo: logoPath || null,
          logoUrlLight: logoPath ? `${imgUrl}/${logoPath}` : defaultLogoLight,
          logoUrlColor: logoPath ? `${imgUrl}/${logoPath}` : defaultLogoColor
        };
        Object.keys(data).forEach(key => {
          if (!settingsObj.hasOwnProperty(key)) {
            settingsObj[key] = data[key];
          }
        });
        return settingsObj;
      } catch (e) {
        // Fallback to defaults
      }
    }
    return {
      shopName: 'KachaBazar',
      currency: 'BDT',
      currencySymbol: '৳',
      language: 'en',
      shopLogo: null,
      logoUrlLight: defaultLogoLight,
      logoUrlColor: defaultLogoColor
    };
  });

  const [loading, setLoading] = useState(() => {
    return !localStorage.getItem('shop_settings');
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings/public');
        if (res.data && res.data.success) {
          const data = res.data.data;
          localStorage.setItem('shop_settings', JSON.stringify(data));
          const logoPath = data.shop_logo;
          const lang = data.language || 'en';
          i18n.changeLanguage(lang);
          const settingsObj = {
            shopName: data.shop_name || 'KachaBazar',
            currency: data.currency || 'BDT',
            currencySymbol: getCurrencySymbol(data.currency || 'BDT'),
            language: lang,
            shopLogo: logoPath || null,
            logoUrlLight: logoPath ? `${imgUrl}/${logoPath}` : defaultLogoLight,
            logoUrlColor: logoPath ? `${imgUrl}/${logoPath}` : defaultLogoColor
          };
          Object.keys(data).forEach(key => {
            if (!settingsObj.hasOwnProperty(key)) {
              settingsObj[key] = data[key];
            }
          });
          setSettings(settingsObj);
        }
      } catch (err) {
        console.error('Failed to load store settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (settings?.language) {
      document.documentElement.lang = settings.language;
    }
  }, [settings?.language]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
          <p className="mt-4 text-emerald-600 font-semibold tracking-wide animate-pulse">
            Loading Store Configuration...
          </p>
        </div>
      </div>
    );
  }

  return (
    <SettingsContext.Provider value={settings}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    return {
      shopName: 'KachaBazar',
      currency: 'BDT',
      currencySymbol: '৳',
      language: 'en',
      shopLogo: null,
      logoUrlLight: defaultLogoLight,
      logoUrlColor: defaultLogoColor
    };
  }
  return context;
}
